import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Alert, AlertTitle, CircularProgress, TextField, Button, Chip, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Switch, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox, Collapse,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { monitorConfigAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };
const KINDS = [{ key: 'prod', label: 'Production', file: 'telegraf.conf' }, { key: 'dt', label: 'Dev/Test', file: 'telegrafDT.conf' }];
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmt = (t) => (t ? new Date(t).toLocaleString() : '-');
let nextId = 1;
const withIds = (entries) => entries.map((e) => ({ ...e, _id: nextId++ }));
const strip = ({ _id, ...e }) => ({ kind: e.kind, text: e.text, note: e.note || null }); // eslint-disable-line no-unused-vars
const URL_OK = /^https?:\/\/[A-Za-z0-9._~:/?#[\]@!$&'()*+,;=%-]{1,500}$/;
const sameLines = (a, b) => JSON.stringify(a.map(strip)) === JSON.stringify(b.map(strip));

// What changed, by web address (the same idea as the server's check)
export function difference(oldEntries, newEntries) {
  const live = (list) => new Set(list.filter((e) => e.kind === 'url').map((e) => e.text));
  const off = (list) => new Set(list.filter((e) => e.kind === 'disabled').map((e) => e.text));
  const [ol, nl, oo, no] = [live(oldEntries), live(newEntries), off(oldEntries), off(newEntries)];
  return {
    added: [...nl].filter((u) => !ol.has(u) && !oo.has(u)), removed: [...ol].filter((u) => !nl.has(u) && !no.has(u)),
    switchedOn: [...nl].filter((u) => oo.has(u)), switchedOff: [...no].filter((u) => ol.has(u)),
  };
}

function ChangeSummary({ changes }) {
  if (!changes) return null;
  const parts = [['added', 'added', 'success'], ['removed', 'removed', 'error'], ['switched_on', 'switched on', 'success'], ['switched_off', 'switched off', 'warning']];
  const any = parts.some(([k]) => (changes[k] || []).length);
  return (
    <Box>
      {!any && <Typography variant="body2" color="text.secondary">{changes.other_lines_changed ? 'Only comments, blank lines or layout differ' : 'No difference'}</Typography>}
      {parts.map(([k, label, color]) => (changes[k] || []).length > 0 && (
        <Box key={k} sx={{ mb: 0.5 }}>
          <Chip size="small" color={color} label={`${changes[k].length} ${label}`} sx={{ mr: 1 }} />
          <Typography variant="caption" component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{changes[k].slice(0, 3).join('  |  ')}{changes[k].length > 3 ? `  | and ${changes[k].length - 3} more` : ''}</Typography>
        </Box>
      ))}
    </Box>
  );
}

const MERGED = '__merged__';
const WORD = { live: 'monitored', off: 'switched off', missing: 'not in the list', comment: 'a broken comment' };

// What was not the same on every server, and what the merged list does about it
function MergeNotes({ merged }) {
  const [open, setOpen] = useState(true);
  return (
    <Alert severity="info" sx={{ mb: 1 }} action={<Button color="inherit" size="small" onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'}</Button>}>
      <AlertTitle>Merged list of {merged.from.join(' and ')}: {merged.live} URLs monitored</AlertTitle>
      A URL that is monitored on any server stays monitored, and one that is only on one server is added to the list. {merged.notes.length} difference{merged.notes.length === 1 ? '' : 's'} found.
      <Collapse in={open}>
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
          {merged.notes.map((n, i) => (
            <li key={`${n.url}${i}`}>
              <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{n.url.replace(/^https?:\/\//, '')}</Typography>
              {': '}{Object.entries(n.servers).map(([h, st]) => `${h} has it ${WORD[st] || st}`).join(', ')}. {n.converted_comment ? 'Turned into a proper switched-off entry' : `In the merged list: ${n.merged === 'live' ? 'monitored' : 'switched off'}`}.
            </li>
          ))}
        </Box>
      </Collapse>
    </Alert>
  );
}

function ServersPanel({ onChanged }) {
  const [servers, setServers] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => { monitorConfigAPI.servers().then((r) => setServers(r.data)).catch((err) => { setServers([]); setError(errText(err, 'Could not load the monitor servers')); }); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true); setError(null);
    try { await monitorConfigAPI.addServer(name.trim()); setAdding(false); setName(''); load(); onChanged(); }
    catch (err) { setError(errText(err, 'Could not add the server')); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true); setError(null);
    try { await monitorConfigAPI.removeServer(removing); setRemoving(null); load(); onChanged(); }
    catch (err) { setError(errText(err, 'Could not remove the server')); setRemoving(null); }
    finally { setBusy(false); }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Monitor servers</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)}>Add server</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Every change is sent to all of these servers, so they always match.</Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {servers === null ? <CircularProgress size={20} /> : (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {servers.map((s) => (
            <Chip key={s.name} color={s.reachable ? 'success' : 'warning'} variant="outlined" onDelete={() => setRemoving(s.name)}
              label={`${s.name} (${s.region})${s.reachable ? '' : ' - not reachable'}`} />
          ))}
          {servers.length === 0 && <Typography variant="body2">No monitor servers are in the list.</Typography>}
        </Box>
      )}
      <Dialog open={adding} onClose={() => setAdding(false)}>
        <DialogTitle>Add a monitor server</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>The server must be running, reachable by Systems Manager and have the same Telegraf folder as the others (C:\Program Files\InfluxData\telegraf).</Typography>
          <TextField autoFocus fullWidth size="small" label="Server name" placeholder="SCS-USW2B-MON02" value={name} onChange={(e) => setName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
          <Button variant="contained" onClick={add} disabled={busy || !name.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!removing} onClose={() => setRemoving(null)}>
        <DialogTitle>Stop managing {removing}?</DialogTitle>
        <DialogContent><Typography variant="body2">The server is not changed. It is only left out of future updates, so its list will no longer be kept in step with the others.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={remove} disabled={busy}>Remove from the list</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

function EntryRow({ entry, onChange, onDelete }) {
  const locked = entry.kind === 'raw';
  const isUrl = entry.kind === 'url' || entry.kind === 'disabled';
  const bad = isUrl && !URL_OK.test((entry.text || '').trim());
  return (
    <TableRow hover sx={{ opacity: entry.kind === 'disabled' ? 0.6 : 1 }}>
      <TableCell padding="checkbox">
        {isUrl && <Tooltip title={entry.kind === 'url' ? 'Being monitored. Switch off to comment it out' : 'Switched off (commented out). Switch on to monitor it'}>
          <Switch size="small" checked={entry.kind === 'url'} onChange={(e) => onChange({ kind: e.target.checked ? 'url' : 'disabled' })} slotProps={{ input: { 'aria-label': `Monitor ${entry.text}` } }} />
        </Tooltip>}
      </TableCell>
      <TableCell sx={{ width: '100%' }}>
        {isUrl && <TextField fullWidth size="small" variant="standard" value={entry.text} error={bad} helperText={bad ? 'Must start with http:// or https:// and have no spaces or quotes' : undefined}
          onChange={(e) => onChange({ text: e.target.value })} slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.8rem' }, 'aria-label': 'Web address' } }} />}
        {entry.kind === 'comment' && <TextField fullWidth size="small" variant="standard" value={entry.text} onChange={(e) => onChange({ text: e.target.value })}
          slotProps={{ htmlInput: { style: { fontStyle: 'italic', fontSize: '0.8rem' }, 'aria-label': 'Comment' } }} InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>#</Typography> }} />}
        {entry.kind === 'blank' && <Typography variant="caption" color="text.disabled">(blank line)</Typography>}
        {locked && <Tooltip title="The portal does not understand this line, so it keeps it exactly as it is"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>{entry.text}</Typography></Tooltip>}
      </TableCell>
      <TableCell padding="checkbox">
        {!locked && <IconButton size="small" aria-label="Remove this line" onClick={onDelete}><DeleteOutlineIcon fontSize="small" /></IconButton>}
      </TableCell>
    </TableRow>
  );
}

function ResultRows({ results }) {
  return (results || []).map((r) => (
    <Box key={r.host} sx={{ mb: 1 }}>
      <Typography variant="body2" fontWeight="bold">{r.host} {r.ok ? <Chip size="small" color="success" label="Updated" /> : <Chip size="small" color={r.skipped ? 'default' : r.applied ? 'warning' : 'error'} label={r.skipped ? 'Not attempted' : r.applied ? 'Changed, check it' : r.rolled_back ? 'Rolled back' : 'Failed'} />}</Typography>
      {r.ok && <Typography variant="caption" display="block">Old file kept as <b>{r.backup}</b>. Service {r.service}.</Typography>}
      {!r.ok && r.error && <Typography variant="caption" display="block" color="error">{r.error}</Typography>}
      {!r.ok && r.backup && <Typography variant="caption" display="block">Backup: {r.backup}</Typography>}
    </Box>
  ));
}

function PushDialog({ kind, entries, expected, onClose, onDone }) {
  const [check, setCheck] = useState(null);
  const [reason, setReason] = useState('');
  const [ticket, setTicket] = useState('');
  const [error, setError] = useState(null);
  const [change, setChange] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const body = useMemo(() => ({ entries: entries.map(strip), expected, reason, ticket: ticket || null }), [entries, expected, reason, ticket]);

  useEffect(() => {
    monitorConfigAPI.check(kind.key, { ...body, reason: 'check' }).then((r) => setCheck(r.data)).catch((err) => setError(errText(err, 'Could not check the list')));
    return () => clearTimeout(timer.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const poll = useCallback((id) => {
    monitorConfigAPI.change(id).then((r) => {
      setChange(r.data);
      if (r.data.status === 'running') timer.current = setTimeout(() => poll(id), 3000);
      else onDone();
    }).catch(() => { timer.current = setTimeout(() => poll(id), 5000); });
  }, [onDone]);

  const send = async () => {
    setBusy(true); setError(null);
    try { const r = await monitorConfigAPI.push(kind.key, body); setChange({ status: 'running' }); poll(r.data.id); }
    catch (err) { setError(errText(err, 'Could not send the update')); }
    finally { setBusy(false); }
  };

  const running = change?.status === 'running';
  const finished = change && !running;
  return (
    <Dialog open onClose={running ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Update {kind.file} on every monitor server</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!check && !error && <CircularProgress size={22} />}
        {check && !change && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>The new list has {check.summary.live} URLs being monitored and {check.summary.switched_off} switched off. This is what will change on each server:</Typography>
            {check.servers.map((s) => (
              <Box key={s.host} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2">{s.host}</Typography>
                {s.ok ? <ChangeSummary changes={s.changes} /> : <Alert severity="error">{s.error}</Alert>}
              </Box>
            ))}
            {check.duplicates.length > 0 && <Alert severity="warning" sx={{ mb: 1 }}>{check.duplicates.length} address(es) appear more than once: {check.duplicates.slice(0, 2).join(', ')}</Alert>}
            <Alert severity="info" sx={{ mb: 2 }}>
              Each server's old file is copied to <b>{kind.file}_date_time_backup</b> in the same folder first, the new file is checked by Telegraf, then the {kind.key === 'prod' ? 'telegraf' : 'TelegrafDT'} service is restarted (a few seconds without monitoring on that server). If the service does not stay running the old file is put back.
            </Alert>
            <TextField fullWidth size="small" label="Why is this change needed?" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mb: 2 }} helperText="At least 5 characters. Kept in the audit trail." />
            <TextField size="small" label="Ticket number (optional)" value={ticket} onChange={(e) => setTicket(e.target.value)} />
          </>
        )}
        {change && (
          <>
            {running && <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}><CircularProgress size={22} /><Typography>Updating the servers one at a time. This takes about a minute per server. Do not close this page.</Typography></Box>}
            {finished && <Alert severity={change.status === 'succeeded' ? 'success' : 'error'} sx={{ mb: 2 }}>{change.status === 'succeeded' ? 'Every monitor server now has the same list.' : (change.error || 'The update did not complete.')}</Alert>}
            <ResultRows results={change.results} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!change && <Button onClick={onClose}>Cancel</Button>}
        {!change && <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={send} disabled={busy || !check || reason.trim().length < 5 || !!error}>Update all servers</Button>}
        {finished && <Button variant="contained" onClick={onClose}>Close</Button>}
      </DialogActions>
    </Dialog>
  );
}

function History({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { monitorConfigAPI.changes({ limit: 15 }).then((r) => setRows(r.data.changes)).catch(() => setRows([])); }, [refreshKey]);
  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>Recent changes</Typography>
      {rows === null ? <CircularProgress size={20} /> : rows.length === 0 ? <Typography variant="body2" color="text.secondary">Nothing has been changed from the portal yet.</Typography> : (
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell padding="checkbox" /><TableCell>When</TableCell><TableCell>Config</TableCell><TableCell>Who</TableCell><TableCell>Why</TableCell><TableCell>Result</TableCell></TableRow></TableHead>
            <TableBody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <TableRow hover>
                    <TableCell padding="checkbox"><IconButton size="small" aria-label={`Show details of change ${r.id}`} onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></TableCell>
                    <TableCell>{fmt(r.requested_at)}</TableCell>
                    <TableCell>{r.file_name}</TableCell>
                    <TableCell>{r.requested_name || r.requested_by}</TableCell>
                    <TableCell>{r.reason}{r.ticket ? ` (${r.ticket})` : ''}</TableCell>
                    <TableCell><Chip size="small" color={r.status === 'succeeded' ? 'success' : r.status === 'failed' ? 'error' : 'default'} label={r.status} /></TableCell>
                  </TableRow>
                  <TableRow><TableCell sx={{ py: 0 }} colSpan={6}>
                    <Collapse in={open === r.id} unmountOnExit>
                      <Box sx={{ py: 1.5 }}>
                        {r.error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{r.error}</Typography>}
                        <ResultRows results={r.results} />
                        {(r.results || []).map((x) => x.changes && <Box key={x.host} sx={{ mb: 1 }}><Typography variant="caption">{x.host}</Typography><ChangeSummary changes={x.changes} /></Box>)}
                      </Box>
                    </Collapse>
                  </TableCell></TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

function Editor({ kind, refreshKey, onPushed }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('');
  const [draft, setDraft] = useState([]);
  const [base, setBase] = useState([]);
  const [filter, setFilter] = useState('');
  const [showOther, setShowOther] = useState(true);
  const [newUrl, setNewUrl] = useState('');
  const [pushing, setPushing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = (await monitorConfigAPI.get(kind.key)).data;
      setData(res);
      const first = res.servers.find((s) => s.ok);
      const chosen = res.servers.find((s) => s.ok && s.host === source);
      const useMerged = !!res.merged && (source === MERGED || !chosen);       // while the servers differ, start from the merge of them
      setSource(useMerged ? MERGED : (chosen || first)?.host || '');
      setDraft(withIds(useMerged ? res.merged.entries : (chosen || first)?.entries || []));
      setBase((chosen || first)?.entries || []);
    } catch (err) { setData(null); setError(errText(err, 'Could not read the monitor servers')); }
    finally { setLoading(false); }
  }, [kind.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, refreshKey]);

  const startFrom = (host) => {
    if (host === MERGED) {
      const first = data.servers.find((x) => x.ok);
      setSource(MERGED); setDraft(withIds(data.merged.entries)); setBase(first ? first.entries : []);
      return;
    }
    const s = data.servers.find((x) => x.host === host);
    setSource(host); setDraft(withIds(s.entries)); setBase(s.entries);
  };
  const change = (id) => (patch) => setDraft((cur) => cur.map((e) => (e._id === id ? { ...e, ...patch } : e)));
  const remove = (id) => () => setDraft((cur) => cur.filter((e) => e._id !== id));
  const add = () => {
    const url = newUrl.trim();
    if (!URL_OK.test(url)) return;
    setDraft((cur) => [...cur, ...withIds([{ kind: 'url', text: url }])]);
    setNewUrl('');
  };

  const dirty = !sameLines(draft, base);
  const unreadable = data?.servers.some((s) => !s.ok);
  const invalid = draft.some((e) => (e.kind === 'url' || e.kind === 'disabled') && !URL_OK.test((e.text || '').trim()));
  const live = draft.filter((e) => e.kind === 'url').length;
  const needsSync = data && !data.in_sync && !unreadable;
  const expected = useMemo(() => Object.fromEntries((data?.servers || []).filter((s) => s.ok).map((s) => [s.host, s.sha])), [data]);
  const term = filter.trim().toLowerCase();
  const shown = draft.filter((e) => {
    if (!showOther && !(e.kind === 'url' || e.kind === 'disabled')) return false;
    return !term || (e.text || '').toLowerCase().includes(term);
  });

  if (loading && !data) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {data && (
        <Paper sx={{ p: 2, mb: 2 }}>
          {data.in_sync && <Alert severity="success" sx={{ mb: 1 }}>Every monitor server has the same list in {data.file}.</Alert>}
          {needsSync && <Alert severity="warning" sx={{ mb: 1 }}><AlertTitle>The servers do not match</AlertTitle>Their lists in {data.file} differ. Choose the list to start from, check it, then update all servers so they match.</Alert>}
          {unreadable && <Alert severity="error" sx={{ mb: 1 }}>A server could not be read, so nothing can be updated until it can: {data.servers.filter((s) => !s.ok).map((s) => `${s.host} (${s.error})`).join('; ')}</Alert>}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {data.servers.map((s) => (
              <Chip key={s.host} color={s.ok ? 'default' : 'error'} variant="outlined"
                label={s.ok ? `${s.host}: ${s.live} monitored, file changed ${fmt(s.modified)}` : `${s.host}: could not be read`} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
            <TextField select size="small" label="Start from the list on" value={source} onChange={(e) => startFrom(e.target.value)} sx={{ minWidth: 260 }} slotProps={NATIVE}>
              {data.merged && <option value={MERGED}>Merge of all servers (recommended while they differ)</option>}
              {data.servers.filter((s) => s.ok).map((s) => <option key={s.host} value={s.host}>{s.host}</option>)}
            </TextField>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Reload from the servers</Button>
            {dirty && <Chip color="warning" label="Unsaved changes" />}
          </Box>
        </Paper>
      )}
      {data && data.merged && source === MERGED && <MergeNotes merged={data.merged} />}
      {data && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
            <TextField size="small" label="Add a URL" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
              sx={{ minWidth: 420, flexGrow: 1 }} placeholder="https://customer.starlimscloud.com/starlims.cus.prod/authentication.getserverstatus.lims2"
              error={!!newUrl && !URL_OK.test(newUrl.trim())} helperText={newUrl && !URL_OK.test(newUrl.trim()) ? 'Must start with http:// or https:// and have no spaces or quotes' : ' '} />
            <Button variant="outlined" startIcon={<AddIcon />} onClick={add} disabled={!URL_OK.test(newUrl.trim())}>Add</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
            <TextField size="small" label="Search the list" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 240 }} />
            <FormControlLabel control={<Checkbox size="small" checked={showOther} onChange={(e) => setShowOther(e.target.checked)} />} label="Show comments and blank lines" />
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>{live} being monitored, {draft.filter((e) => e.kind === 'disabled').length} switched off</Typography>
            <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setPushing(true)} disabled={(!dirty && !needsSync) || invalid || unreadable || live === 0}>
              Review and update all servers
            </Button>
          </Box>
          {invalid && <Alert severity="error" sx={{ mb: 1 }}>One or more web addresses are not valid. Fix them before updating.</Alert>}
          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow><TableCell padding="checkbox">On</TableCell><TableCell>The urls list in {data.file}</TableCell><TableCell padding="checkbox" /></TableRow></TableHead>
              <TableBody>
                {shown.map((e) => <EntryRow key={e._id} entry={e} onChange={change(e._id)} onDelete={remove(e._id)} />)}
                {shown.length === 0 && <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary">Nothing matches.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {pushing && <PushDialog kind={kind} entries={draft} expected={expected} onClose={() => { setPushing(false); load(); }} onDone={onPushed} />}
    </Box>
  );
}

function MonitorServers() {
  const { isAdmin, loading } = usePermissions();
  const [tab, setTab] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (!isAdmin()) return <Alert severity="info">Only Cloud Services administrators can manage the monitor servers.</Alert>;
  const kind = KINDS[tab];
  return (
    <Box>
      <Typography variant="h4" gutterBottom>Monitor Servers</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The URLs the monitor servers check for each customer (they feed Customer Uptime). Only the url lists are shown. Change a list here and the portal updates the same file on every monitor server, backing up the old file first.
      </Typography>
      <ServersPanel onChanged={bump} />
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        {KINDS.map((k) => <Tab key={k.key} label={`${k.label} (${k.file})`} />)}
      </Tabs>
      <Editor key={kind.key} kind={kind} refreshKey={refreshKey} onPushed={bump} />
      <History refreshKey={refreshKey} />
    </Box>
  );
}

export default MonitorServers;
